// Channels panel — renders the per-channel rows for the active document.
//
// Photoshop's Channels panel always lists the composite + each per-channel:
//   RGB:    RGB | Red | Green | Blue       (Ctrl+2 / +3 / +4 / +5)
//   CMYK:   CMYK | Cyan | Magenta | Yellow | Black
//   Lab:    Lab  | Lightness | a | b
//   Grayscale: Gray
//   + any alpha channels created via Save Selection
//   + any spot channels
//
// Closes #154: previously only a single "RGB" composite row was rendered.
//
// The doc has no explicit `colorMode` field today — we default to RGB but
// expose a `colorMode` argument so the rest of the app can pass it in when
// Image > Mode is supported on the document model.
import type { EditorState } from "../state/EditorState";
import type { AlphaChannel } from "../../core/selection/Selection";

export type ChannelColorMode = "rgb" | "cmyk" | "lab" | "grayscale";

export interface ChannelDescriptor {
  /** Stable id (used as data attribute and channel-show event payload). */
  readonly id: string;
  /** Display name (e.g. "Red", "Cyan", "Lightness"). */
  readonly name: string;
  /** Whether this row is the composite (RGB/CMYK/Lab heading row). */
  readonly composite: boolean;
  /** Shortcut hint to display on the right (e.g. "Ctrl+3"). */
  readonly shortcut: string;
  /**
   * Thumbnail rendering hint — a CSS background (linear-gradient or color).
   * The composite row uses no thumb.
   */
  readonly thumbBackground: string;
}

export interface ChannelsPanelDeps {
  state: EditorState;
  /** Active color mode. Defaults to "rgb" if not provided. */
  colorMode?: ChannelColorMode;
  /** Doc-name → alpha channel list. */
  alphaChannelsByDocName?: Map<string, AlphaChannel[]>;
  /** Channel show callback — called when user clicks a channel row to display it. */
  onChannelShow?: (channelId: string) => void;
  /** Footer: load channel as selection. */
  onLoadAsSelection?: (channelId: string) => void;
  /** Footer: save current selection as new alpha channel. */
  onSaveAsSelection?: () => void;
  /** Footer: new (alpha) channel. */
  onNewChannel?: () => void;
  /** Footer: delete current channel (only meaningful for alpha). */
  onDeleteChannel?: (channelId: string) => void;
  /** Current active channel id (which row should be highlighted as "showing"). */
  activeChannelId?: string;
}

let _deps: ChannelsPanelDeps | null = null;
let _unsub: (() => void) | null = null;
let _keydownHandler: ((e: KeyboardEvent) => void) | null = null;

export function __resetChannelsPanelForTest(): void {
  if (_unsub) _unsub();
  _unsub = null;
  _deps = null;
  if (_keydownHandler) {
    document.removeEventListener("keydown", _keydownHandler);
    _keydownHandler = null;
  }
}

/** Compute the list of channels for a given color mode. */
export function channelsForMode(mode: ChannelColorMode): ChannelDescriptor[] {
  switch (mode) {
    case "grayscale":
      return [
        { id: "gray", name: "Gray", composite: false, shortcut: "Ctrl+2", thumbBackground: "linear-gradient(#fff, #000)" },
      ];
    case "cmyk":
      return [
        { id: "cmyk", name: "CMYK", composite: true, shortcut: "Ctrl+2", thumbBackground: "" },
        { id: "cyan", name: "Cyan", composite: false, shortcut: "Ctrl+3", thumbBackground: "linear-gradient(#00ffff, #000)" },
        { id: "magenta", name: "Magenta", composite: false, shortcut: "Ctrl+4", thumbBackground: "linear-gradient(#ff00ff, #000)" },
        { id: "yellow", name: "Yellow", composite: false, shortcut: "Ctrl+5", thumbBackground: "linear-gradient(#ffff00, #000)" },
        { id: "black", name: "Black", composite: false, shortcut: "Ctrl+6", thumbBackground: "linear-gradient(#999, #000)" },
      ];
    case "lab":
      return [
        { id: "lab", name: "Lab", composite: true, shortcut: "Ctrl+2", thumbBackground: "" },
        { id: "lightness", name: "Lightness", composite: false, shortcut: "Ctrl+3", thumbBackground: "linear-gradient(#fff, #000)" },
        { id: "a", name: "a", composite: false, shortcut: "Ctrl+4", thumbBackground: "linear-gradient(#ff66cc, #66ff66)" },
        { id: "b", name: "b", composite: false, shortcut: "Ctrl+5", thumbBackground: "linear-gradient(#ffff66, #6666ff)" },
      ];
    case "rgb":
    default:
      return [
        { id: "rgb", name: "RGB", composite: true, shortcut: "Ctrl+2", thumbBackground: "" },
        { id: "red", name: "Red", composite: false, shortcut: "Ctrl+3", thumbBackground: "linear-gradient(#ff0000, #000)" },
        { id: "green", name: "Green", composite: false, shortcut: "Ctrl+4", thumbBackground: "linear-gradient(#00ff00, #000)" },
        { id: "blue", name: "Blue", composite: false, shortcut: "Ctrl+5", thumbBackground: "linear-gradient(#0000ff, #000)" },
      ];
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}

/**
 * Ctrl/Cmd+2 through Ctrl/Cmd+6 switch the visible channel, mirroring
 * Photoshop's Ctrl+~/1-9 channel-switching convention (i212). The digit is
 * mapped positionally onto whatever channelsForMode() returns for the
 * current color mode — Ctrl+2 always selects the first row (the composite,
 * or "Gray" for grayscale docs), Ctrl+3 the second, and so on — so this
 * naturally respects RGB/CMYK/Lab/Grayscale differences without hardcoding
 * channel ids.
 */
function handleChannelShortcutKeydown(e: KeyboardEvent): void {
  if (!_deps) return;
  if (!(e.ctrlKey || e.metaKey)) return;
  const match = /^Digit([2-6])$/.exec(e.code);
  if (!match) return;
  const digit = Number(match[1]);
  const mode = _deps.colorMode ?? "rgb";
  const channels = channelsForMode(mode);
  const channel = channels[digit - 2];
  if (!channel) return;
  e.preventDefault();
  _deps.onChannelShow?.(channel.id);
}

/** Mount / render the Channels panel. Subscribes to state changes once. */
export function renderChannelsPanel(deps: ChannelsPanelDeps): void {
  _deps = deps;
  if (!_unsub) {
    _unsub = deps.state.subscribe(() => {
      if (_deps) renderInternal(_deps);
    });
  }
  if (!_keydownHandler) {
    _keydownHandler = handleChannelShortcutKeydown;
    document.addEventListener("keydown", _keydownHandler);
  }
  renderInternal(deps);
}

function renderInternal(deps: ChannelsPanelDeps): void {
  const root = document.querySelector<HTMLElement>("#channels-list");
  if (!root) return;
  const doc = deps.state.doc;
  if (!doc) {
    root.innerHTML = '<div class="muted" style="padding:12px;text-align:center">No document</div>';
    return;
  }

  const mode = deps.colorMode ?? "rgb";
  const channels = channelsForMode(mode);
  const alphas = deps.alphaChannelsByDocName?.get(doc.name) ?? [];
  const activeId = deps.activeChannelId ?? channels[0]?.id ?? "rgb";

  const parts: string[] = [];

  for (const ch of channels) {
    const isActive = ch.id === activeId;
    const thumb = ch.composite
      ? ""
      : `<div class="channel-row__thumb" style="background:${ch.thumbBackground}"></div>`;
    parts.push(`
      <div class="channel-row${isActive ? " is-active" : ""}" data-channel-id="${escapeHtml(ch.id)}">
        <button class="channel-row__vis" data-vis="${escapeHtml(ch.id)}" title="Toggle visibility">
          <svg class="ico-xs"><use href="#i-eye"/></svg>
        </button>
        ${thumb}
        <span class="channel-row__name">${escapeHtml(ch.name)}</span>
        <span class="channel-row__shortcut">${escapeHtml(ch.shortcut)}</span>
      </div>
    `);
  }

  // Alpha channels
  for (const a of alphas) {
    const id = `alpha:${a.id}`;
    const isActive = id === activeId;
    parts.push(`
      <div class="channel-row channel-row--alpha${isActive ? " is-active" : ""}" data-channel-id="${escapeHtml(id)}">
        <button class="channel-row__vis" data-vis="${escapeHtml(id)}" title="Toggle visibility">
          ${a.visible ? '<svg class="ico-xs"><use href="#i-eye"/></svg>' : ""}
        </button>
        <div class="channel-row__thumb" style="background:rgba(${a.color.r},${a.color.g},${a.color.b},${a.color.a})"></div>
        <span class="channel-row__name">${escapeHtml(a.name)}</span>
      </div>
    `);
  }

  root.innerHTML = parts.join("");

  // Wire row click handlers
  root.querySelectorAll<HTMLElement>(".channel-row").forEach((row) => {
    const id = row.getAttribute("data-channel-id");
    if (!id) return;

    row.addEventListener("click", (ev) => {
      const visBtn = (ev.target as HTMLElement).closest<HTMLElement>("[data-vis]");
      if (visBtn) {
        ev.stopPropagation();
        // visibility toggle is mostly meaningful for alpha channels; for RGB
        // we dispatch the show-event same as a row click so users can mute a
        // single channel.
        deps.onChannelShow?.(visBtn.getAttribute("data-vis") ?? id);
        return;
      }
      deps.onChannelShow?.(id);
    });
  });

  // Wire footer buttons (delegated to the parent .channels-body)
  const footerHost = root.closest<HTMLElement>(".channels-body");
  if (footerHost) {
    footerHost.querySelector<HTMLElement>("#channel-load-selection")?.addEventListener("click", () => {
      deps.onLoadAsSelection?.(activeId);
    }, { once: true });
    footerHost.querySelector<HTMLElement>("#channel-save-selection")?.addEventListener("click", () => {
      deps.onSaveAsSelection?.();
    }, { once: true });
    footerHost.querySelector<HTMLElement>("#channel-new-alpha")?.addEventListener("click", () => {
      deps.onNewChannel?.();
    }, { once: true });
    footerHost.querySelector<HTMLElement>("#channel-delete")?.addEventListener("click", () => {
      deps.onDeleteChannel?.(activeId);
    }, { once: true });
  }
}

/** Test helper: count rendered rows (composite + per-channel + alphas). */
export function countRowsForTest(): number {
  const root = document.querySelector<HTMLElement>("#channels-list");
  if (!root) return 0;
  return root.querySelectorAll(".channel-row").length;
}
